import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useGuestAuth } from '../contexts/GuestAuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import FavoriteButton from './FavoriteButton';
import { Sparkles, Bed, Bath, Users, MapPin, Loader2 } from 'lucide-react';

/**
 * Personalized Recommendations Component
 * 
 * Shows property recommendations based on:
 * - For signed-in users: browsing history and preferences
 * - For anonymous users: featured properties
 */
const Recommendations = ({ className = '' }) => {
  const { user, isAuthenticated, getRecommendations } = useGuestAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations();
  }, [isAuthenticated]);

  const fetchRecommendations = async () => {
    try {
      const data = await getRecommendations();
      setRecommendations(data.recommendations || []);
      setReason(data.reason || 'featured');
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`py-8 ${className}`}>
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  const getReasonText = () => {
    if (reason === 'similar_to_viewed') {
      return 'Based on properties you viewed';
    }
    return 'Featured properties you might like';
  };

  return (
    <section className={`py-12 bg-slate-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-6 w-6 text-amber-500" />
              <h2 className="text-2xl font-bold text-slate-900">
                {isAuthenticated ? 'Recommended for You' : 'You Might Also Like'}
              </h2>
            </div>
            <p className="text-slate-600">{getReasonText()}</p>
          </div>
          <Link to="/properties">
            <Button variant="outline">View All</Button>
          </Link>
        </div>

        {/* Recommendations Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {recommendations.map((property) => (
            <Card 
              key={property.id} 
              className="overflow-hidden hover:shadow-lg transition-shadow group"
            >
              <Link to={`/property/${property.id}`}>
                <div className="relative aspect-[4/3]">
                  <img
                    src={property.images?.[0]}
                    alt={property.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {/* Favorite Button */}
                  <div className="absolute top-2 right-2 z-10">
                    <FavoriteButton propertyId={property.id} />
                  </div>
                  {/* Price Badge */}
                  <div className="absolute bottom-2 left-2 bg-white px-2 py-1 rounded-full shadow text-sm">
                    <span className="font-bold text-slate-900">${property.price_per_night}</span>
                    <span className="text-xs text-slate-500">/night</span>
                  </div>
                </div>
                <CardContent className="p-3">
                  <h3 className="font-semibold text-slate-900 mb-1 truncate group-hover:text-amber-600 transition-colors">
                    {property.name}
                  </h3>
                  <div className="flex items-center text-slate-500 text-xs mb-2">
                    <MapPin className="h-3 w-3 mr-1" />
                    <span className="truncate">Washington DC</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-600">
                    <div className="flex items-center gap-1">
                      <Bed className="h-3 w-3" />
                      <span>{property.bedrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Bath className="h-3 w-3" />
                      <span>{property.bathrooms}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{property.sleeps}</span>
                    </div>
                  </div>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Recommendations;
